import { useState, useEffect, useRef, useCallback, useMemo, RefObject } from 'react';
import { FinalCountdown } from '@/store/videoFeatures/types';
import { normalizeRedirectUrl } from '@/lib/redirect-url';

/**
 * Contagem Final Inteligente.
 *
 * Gatilho: evento nativo `ended` do <video> — o overlay aparece DEPOIS que o
 * vídeo termina, não "N segundos antes do fim". `countdownSeconds` é a DURAÇÃO
 * da contagem, não o momento de início.
 *
 * REGRA DO OVERLAY (a mesma no front e no player) — o card NUNCA pode ficar
 * sem saída:
 *
 *   canGo         = redirectMode !== 'warning' && redirectUrl válido (http/https)
 *   botão         = showButton && canGo — e ele só SOME quando a navegação
 *                   realmente aconteceu, nunca só por a contagem zerar
 *   hasRedirected = true apenas se a navegação de fato ocorreu; em nova aba,
 *                   só se `window.open()` devolveu uma janela (pop-up liberado)
 *   dispensar     = quando a contagem zerou e `!hasRedirected` — cobre o modo
 *                   aviso, a URL vazia/inválida e o pop-up bloqueado
 *
 * O botão "Ir Agora" usa `buttonAction`, que é um campo separado do
 * `linkOpenMode`: o usuário pode querer nova aba no clique e mesma aba no
 * redirecionamento automático.
 *
 * `isPreviewMode` desarma a navegação: dentro do editor o <VideoPlayer> é o
 * player real, e sem essa trava o fim do vídeo de preview arrastaria o
 * dashboard inteiro para a página de vendas, levando junto edições não salvas.
 */
export function useFinalCountdown(
  videoRef: RefObject<HTMLVideoElement>,
  config?: FinalCountdown,
  isPreviewMode = false
) {
  const [showOverlay, setShowOverlay] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  // Só vira true quando a navegação ACONTECEU (mesma aba, ou nova aba que
  // realmente abriu). É o que faz o botão sumir e o texto virar
  // "Redirecionando..." — e o que mantém a saída disponível quando falha.
  const [hasRedirected, setHasRedirected] = useState(false);
  // Guarda contra disparo duplo do redirecionamento (StrictMode monta e
  // remonta os efeitos em desenvolvimento).
  const autoFiredRef = useRef(false);

  // URL saneada: documentos antigos podem trazer `javascript:` ou uma URL sem
  // esquema. `null` = não há para onde ir.
  const safeUrl = useMemo(
    () => normalizeRedirectUrl(config?.redirectUrl),
    [config?.redirectUrl]
  );

  // Existe destino para o redirecionamento automático?
  const canGo = !!safeUrl && config?.redirectMode !== 'warning';

  const navigate = useCallback(
    (mode: 'sameTab' | 'newTab'): boolean => {
      if (!safeUrl) return false;
      // No preview do editor nada navega de verdade.
      if (isPreviewMode) return false;
      if (mode === 'newTab') {
        // `noopener`/`noreferrer` ficam FORA da feature string de propósito:
        // por spec do HTML, window.open com qualquer um dos dois devolve SEMPRE
        // null — aberto ou bloqueado —, e sem retorno utilizável não há como
        // distinguir os dois casos. `hasRedirected` depende exatamente dessa
        // distinção (pop-up bloqueado precisa manter o botão de pé), então a
        // proteção vem de zerar `win.opener` logo em seguida. É a mesma solução
        // do player (js/runner.js, `finalCountdownGo`), para as camadas não
        // divergirem. Tradeoff assumido: existe uma janela mínima, antes do
        // `opener = null`, em que a página de destino poderia ler
        // `window.opener` — o `noopener` nativo não tem essa janela.
        const win = window.open(safeUrl, '_blank');
        // Pop-up bloqueado devolve null: não podemos dar a navegação por feita,
        // senão o botão some e o espectador fica sem saída.
        if (!win) return false;
        try {
          win.opener = null;
        } catch {
          // Navegador que trata `opener` como somente-leitura: a aba já abriu,
          // e a navegação continua valendo como feita.
        }
        return true;
      }
      window.location.href = safeUrl;
      return true;
    },
    [safeUrl, isPreviewMode]
  );

  // Redirecionamento automático (fim da contagem)
  const handleRedirect = useCallback(() => {
    if (!canGo) return;
    const ok = navigate(config?.linkOpenMode === 'newTab' ? 'newTab' : 'sameTab');
    if (ok) setHasRedirected(true);
  }, [canGo, config?.linkOpenMode, navigate]);

  // Clique no botão "Ir Agora": antecipa a navegação. O clique é gesto do
  // usuário, então passa pelo bloqueador de pop-up mesmo depois de o
  // redirecionamento automático ter sido barrado.
  const handleSkipCountdown = useCallback(() => {
    const ok = navigate(config?.buttonAction === 'newTab' ? 'newTab' : 'sameTab');
    if (ok) setHasRedirected(true);
  }, [config?.buttonAction, navigate]);

  // Dispensa o overlay e devolve o player. É a saída dos estados em que
  // ninguém vai navegar (modo aviso, URL vazia/inválida, pop-up bloqueado).
  const handleDismissCountdown = useCallback(() => {
    setShowOverlay(false);
    setTimeRemaining(0);
    autoFiredRef.current = false;
  }, []);

  // Monitorar ended event
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !config?.enabled) return;

    const handleEnded = () => {
      autoFiredRef.current = false;
      setHasRedirected(false);
      setTimeRemaining(config.countdownSeconds);
      setShowOverlay(true);
    };

    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [config, videoRef]);

  // Countdown timer
  useEffect(() => {
    if (!showOverlay || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [showOverlay, timeRemaining]);

  // Navega quando a contagem zera. Fica fora do updater do setState de
  // propósito: efeito colateral dentro de um updater roda duas vezes em
  // StrictMode e abriria duas abas.
  useEffect(() => {
    if (!showOverlay || timeRemaining > 0 || autoFiredRef.current) return;
    autoFiredRef.current = true;
    handleRedirect();
  }, [showOverlay, timeRemaining, handleRedirect]);

  return {
    showOverlay,
    timeRemaining,
    handleRedirect,
    handleSkipCountdown,
    handleDismissCountdown,
    // Quem monta o overlay usa estes dois para aplicar a regra acima.
    countdownCanGo: canGo,
    countdownHasRedirected: hasRedirected
  };
}
